import styles from './Components.module.scss';

import { Box, IconButton, Theme, useMediaQuery } from '@mui/material';
import React, { useCallback, useMemo, useRef } from 'react';
import { ColumnInstance, TableInstance } from 'react-table';

import { ReactComponent as ColumnsIcon } from '@work4all/assets/icons/columns.svg';

import { ResizableArea } from '@work4all/components/lib/components/ResizableArea';
import {
  ColumnVisibilityContext,
  useColumnVisibilityManager,
} from '@work4all/components/lib/dataDisplay/basic-table/hooks/useColumnVisibility';
import { SELECTION_COLUMN_ID } from '@work4all/components/lib/dataDisplay/basic-table/utils/makeRowsSelectable';

import { CustomFieldsConfigProvider } from '@work4all/data/lib/custom-fields';
import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';
import { remToPx } from '@work4all/data/lib/hooks/useRemToPx';

import { Article } from '@work4all/models/lib/Classes/Article.entity';
import { ServiceContract } from '@work4all/models/lib/Classes/ServiceContract.entity';
import { AddServiceContractPosTypeOfPos } from '@work4all/models/lib/Enums/AddServiceContractPosTypeOfPos.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { PathsOf } from '@work4all/utils/lib/paths-of/paths-of';

import { ColumnsVisibility } from '../../../../../../../../../components/data-tables/table/components/toolbar/components/columns-visibility';
import { settings, useSetting } from '../../../../../../../../../settings';
import { useFormContextPlus } from '../../../../../../../form-plus/use-form-context-plus';
import { useMaskContext } from '../../../../../../hooks/mask-context';
import { withAutoDisable } from '../../../../../../utils/with-auto-disable';
// ToDo: WW-7459 Move to shared shadow folder (4 below)
import { SelectedPositionProvider } from '../../../../../erp/components/tab-panels/positions/components/hooks/use-selected-position';
import { RemovePositionButton } from '../../../../../erp/components/tab-panels/positions/components/remove-position-button/RemovePositionButton';
import { RowSizePicker } from '../../../../../erp/components/tab-panels/positions/components/row-size-picker/RowSizePicker';
import { handleOrderChange } from '../../../../../erp/components/tab-panels/positions/components/utils/handle-order-change';

import { ClonePositionButton } from './clone-position-button/ClonePositionButton';
import { IEditTable } from './edit-table/types';
import { usePositionState } from './hooks/use-position-state';
import { usePositionsColumns } from './hooks/use-positions-columns';
import { NewPostitionButton } from './new-position-button/NewPostitionButton';
import { PositionPicker } from './position-picker/PositionPicker';
import { PositionsSettings } from './positions-settings/PositionsSettings';
import {
  ForwardedEditTableProps,
  IPositionsTableProps,
  PositionsTable,
} from './positions-table/PositionsTable';
import { ServiceContractPriceGroupPicker } from './price-group-picker/ServiceContractPriceGroupPicker';

interface IPositionsProps
  extends ForwardedEditTableProps,
    Pick<IPositionsTableProps, 'showPositionMask' | 'disabled' | 'singleLine'> {
  showArticleSelectionBig?: boolean;
}

export type ServiceContractPaymentData = PathsOf<ServiceContract, 3>;

const emptyColumns: ColumnInstance[] = [];

export const Components: React.FC<IPositionsProps> = (props) => {
  const {
    allowedColumns,
    disabled = false,
    showArticleSelectionBig = true,
    showPositionMask = true,
    showBorders,
    showSelectionColumn,
    singleLine,
  } = props;

  const editTableRef = useRef<IEditTable>(null);
  const horizontalScrollRef = useRef<HTMLDivElement>(null);
  const { entity } = useMaskContext();

  const { watch } = useFormContextPlus<ServiceContractPaymentData>();

  const previewSize = useSetting(settings.serviceContractArticleSize());
  const serviceContractShowArticle = useSetting(
    settings.serviceContractShowArticle()
  );
  const serviceContractRowSize = useSetting(
    settings.serviceContractPositionsRowSize()
  );
  const serviceContractDefaultPositionKind = useSetting(
    settings.serviceContractDefaultPositionKind()
  );

  const tableInstanceRef = useRef<TableInstance>(null);

  const columnsPopoverState = usePopoverState();
  const openColumnsPopover = columnsPopoverState.handleClick;

  const columnSettings = useSetting(settings.serviceContractPositionsColumn());
  const handleVisibilityChange = useCallback(
    (columns: string[]) => {
      if (!columns.filter((x) => SELECTION_COLUMN_ID !== x).length) return;
      columnSettings.set({
        ...columnSettings.value,
        order: columns,
        visibility: columns,
      });
    },
    [columnSettings]
  );

  const columnsVisibilityManager = useColumnVisibilityManager({
    tableInstanceRef,
    onVisibilityChange: handleVisibilityChange,
    orderColumns: handleOrderChange,
    disabledColumns: ['longtext', 'number'],
  });

  const {
    positions,
    total,
    onAddComponent,
    onCloneComponent,
    onRemoveComponent,
    onMoveComponent,
    onEditComponent,
  } = usePositionState({
    editTableRef,
    entity,
    isSimpleMode: !showBorders,
  });

  const onAddSpecial = useCallback(
    (positionType: AddServiceContractPosTypeOfPos, article: Article) => {
      if (article) {
        onAddComponent({
          typeOfPosition: positionType,
          defaultTypeOfBill:
            serviceContractDefaultPositionKind.value ?? undefined,
          article,
          articleIds: [article.id],
        });
      } else {
        onAddComponent({
          typeOfPosition: positionType,
        });
      }
      if (horizontalScrollRef?.current)
        horizontalScrollRef.current.scrollLeft = 0;
    },
    [onAddComponent, serviceContractDefaultPositionKind.value]
  );

  const { columns, resetColumns } = usePositionsColumns({
    tableInstanceRef,
    allowedColumns,
    total,
    onAddComponent,
    onEditComponent,
    onRemoveComponent,
    disabled,
    editTableRef,
  });

  const isViewportDownMd = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md')
  );

  const onAddOnArticlePicker = useCallback(
    (article) => {
      onAddComponent({
        typeOfPosition: AddServiceContractPosTypeOfPos.DEFAULT,
        defaultTypeOfBill:
          serviceContractDefaultPositionKind.value ?? undefined,
        article,
        articleIds: [article.id],
      });
    },
    [onAddComponent, serviceContractDefaultPositionKind.value]
  );

  const onPickPosition = useCallback(
    (article?: Article | string) => {
      if (typeof article === 'string') {
        onAddComponent({
          typeOfPosition: AddServiceContractPosTypeOfPos.HEADLINE,
          defaultText: article,
        });
      } else if (article) {
        onAddComponent({
          typeOfPosition: AddServiceContractPosTypeOfPos.DEFAULT,
          defaultText: article.id ? undefined : article?.name,
          article,
          articleIds: [article.id],
        });
      } else {
        onAddComponent({
          typeOfPosition: AddServiceContractPosTypeOfPos.TEXTLINE,
        });
      }
    },
    [onAddComponent]
  );

  const mainAddress = watch('mainAddress');

  const customerId = mainAddress?.businessPartner?.id;

  const positionsTable = useMemo(() => {
    return (
      <PositionsTable
        refs={{
          editTable: editTableRef,
          scroll: horizontalScrollRef,
        }}
        columns={columns}
        tableInstanceRef={tableInstanceRef}
        disabled={disabled}
        positions={positions ?? []}
        onMoveComponent={onMoveComponent}
        onPickPosition={onPickPosition}
        onRemoveComponent={onRemoveComponent}
        onEditComponent={onEditComponent}
        showPositionMask={showPositionMask}
        showBorders={showBorders}
        allowedColumns={allowedColumns}
        onAddComponent={onAddComponent}
        showSelectionColumn={showSelectionColumn}
        singleLine={singleLine}
        total={total}
      />
    );
  }, [
    columns,
    disabled,
    positions,
    onAddComponent,
    onMoveComponent,
    onPickPosition,
    onRemoveComponent,
    onEditComponent,
    showPositionMask,
    showBorders,
    allowedColumns,
    showSelectionColumn,
    singleLine,
    total,
  ]);

  return (
    <SelectedPositionProvider>
      <ColumnVisibilityContext.Provider value={columnsVisibilityManager}>
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div className={styles['tableWrapper']}>
            {!isViewportDownMd && showArticleSelectionBig && (
              <Box
                padding="0.5rem 1.75rem"
                display="flex"
                justifyContent="space-between"
              >
                <Box display="flex" alignItems="center" gap="0.5rem">
                  <NewPostitionButton
                    disabled={disabled}
                    onAddSpecial={onAddSpecial}
                    editTableRef={editTableRef}
                  />
                  <RemovePositionButton
                    disabled={disabled}
                    onRemovePosition={onRemoveComponent}
                  />
                  <ClonePositionButton
                    disabled={disabled}
                    onClonePosition={onCloneComponent}
                    positions={positions}
                  />
                </Box>

                <Box display="flex">
                  <ServiceContractPriceGroupPicker />
                  <Box width="0.5rem" />
                  <RowSizePicker
                    value={serviceContractRowSize.value}
                    onChange={serviceContractRowSize.set}
                  />
                  <IconButton
                    color="primary"
                    onClick={(event) => {
                      return openColumnsPopover(event);
                    }}
                  >
                    <ColumnsIcon />
                  </IconButton>
                  <PositionsSettings />
                </Box>
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                flex: 1,
                borderBottom: '1px solid var(--ui04)',
                overflow: 'auto',
                minHeight: 0,
              }}
            >
              {positionsTable}
            </Box>

            <ColumnsVisibility
              popoverState={columnsPopoverState}
              visibleColumns={
                tableInstanceRef.current?.allColumns ?? emptyColumns
              }
              onResetColumns={resetColumns}
            />
          </div>
          {!isViewportDownMd && showArticleSelectionBig && (
            <ResizableArea
              handles="top"
              className={styles.resizable}
              direction="vertical"
              size={
                serviceContractShowArticle.value
                  ? previewSize.value
                  : { height: remToPx(3.25) }
              }
              onResize={(size) => {
                previewSize.set(size);
                serviceContractShowArticle.set(true);
              }}
              maxHeight={1000}
              minHeight={
                serviceContractShowArticle.value ? remToPx(8.5) : remToPx(3.25)
              }
            >
              <PositionPicker
                disabled={disabled}
                className={styles.picker}
                onAddStandard={onAddOnArticlePicker}
                onGroupDoubleClick={onPickPosition}
                customerId={customerId}
              />
            </ResizableArea>
          )}
        </Box>
      </ColumnVisibilityContext.Provider>
    </SelectedPositionProvider>
  );
};

const ComponentsWithCustomColumns = (props: IPositionsProps) => {
  return (
    <CustomFieldsConfigProvider entity={Entities.serviceContractPosition}>
      <Components {...props} />
    </CustomFieldsConfigProvider>
  );
};

export const ComponentsWithAutoDisable = withAutoDisable(
  React.memo(ComponentsWithCustomColumns)
);
